'use client'

import { Button } from '@lastbrand/web-ui/button'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import { LoaderOverlayWrapperDS } from '@src/components/blocks/loader-overlay-wrapper-ds'
import { LoaderWrapperDS } from '@src/components/blocks/loader-wrapper-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'

import type { ReactNode } from 'react'

// Grid sizing mirrors sub-collection-section-ds: card max width driven by DS spacing token vars.
const PRODUCT_CARD_MAX_WIDTH_DESKTOP = 'calc(var(--web-ui-spacing-space-56) * 10)'

// Vertical spacing on the SectionContentContainer band (gutters + 1504px frame come from it).
const wishListBandCss = css({
  pb: 'space-80',
  position: 'relative',
})

const wishListGridCss = css.raw({
  display: 'grid',
  w: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  justifyItems: 'center',
  columnGap: 'space-16',
  rowGap: { base: 'space-32' },
  '& > *': {
    w: 'full',
    maxW: { lg: PRODUCT_CARD_MAX_WIDTH_DESKTOP },
  },
  mt: { base: 'space-48', lg: 'space-80' },
})

// App-install banner present: the heading row absorbs the top spacing, so the grid tucks up.
// Mirrors the legacy `.wishListWrapperCompact` (2rem mobile / 3.2rem desktop).
const wishListGridCompactCss = css.raw({
  mt: { base: 'space-20', lg: 'space-32' },
})

/** Per-item column: card on top, the desktop add-to-bag button pinned under it. */
export const wishListItemWrapperCss = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
})

const emptyWishlistCss = css({
  textAlign: 'center',
  mt: { base: '70px', lg: 'space-100' },
})

const emptyWishlistTitleCss = css({
  textStyle: 'title-24-dec',
  mb: 'space-20',
})

/**
 * DS wishlist page layout — the band, the responsive grid, the loader states and the empty state.
 *
 * Shared by BOTH DS wishlist lists on purpose: the legacy-data one (`wishlist-item-list-ds.tsx`,
 * rendering `ProductCard`) and the SVD one (`wishlist-item-list-svd-ds.tsx`, rendering
 * `SvdProductCard`). They differ only in the tile and its add-to-bag action, so keeping the layout
 * here is what stops the two grids drifting into the misalignment this was extracted to fix
 * (STRFRNT-16908): the SVD list previously reused the **legacy SCSS** grid, whose columns are sized
 * in `rem` for the SCSS card body, while `SvdProductCard` switches to its DS body on a DS page and
 * expects the grid to size it (`maxWidth` / `className` are deliberately no-ops there).
 *
 * Sizing therefore comes from this grid and nowhere else — do not add width props to the tiles.
 */
export function WishlistGridDs({
  isLoading = false,
  hasData,
  children,
}: {
  isLoading?: boolean
  hasData: boolean
  children: ReactNode
}) {
  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })

  const content = (
    <SectionContentContainer className={wishListBandCss}>
      {hasData ? (
        <div
          className={css(wishListGridCss, shouldRenderAppInstallAccounts ? wishListGridCompactCss : {})}
          data-testid={TEST_LOCATORS.WISHLIST_LIST.CONTAINER}
        >
          {children}
        </div>
      ) : (
        <EmptyWishListDs />
      )}
    </SectionContentContainer>
  )

  if (isLoading && !hasData) {
    return (
      <LoaderWrapperDS showLoader minHeight="100vh">
        {content}
      </LoaderWrapperDS>
    )
  }

  if (isLoading && hasData) {
    return <LoaderOverlayWrapperDS isLoading>{content}</LoaderOverlayWrapperDS>
  }

  return content
}

export function EmptyWishListDs() {
  const router = useRouter()
  const { t } = useTranslation('wishlist')
  return (
    <div className={emptyWishlistCss} data-testid={TEST_LOCATORS.WISHLIST_LIST.EMPTY}>
      <h2 className={emptyWishlistTitleCss}>{t('your_wishlist_is_empty')}</h2>
      <Button
        variant="secondary"
        size="lg"
        data-testid={TEST_LOCATORS.WISHLIST_LIST.SHOP_NOW}
        css={{ px: 'space-80', mx: 'auto' }}
        onClick={() => {
          // [STRFRNT-11724] /women works for all regions including UK — [...slug] route handles it universally.
          router.push('/women')
        }}
      >
        {t('shop_now')}
      </Button>
    </div>
  )
}
